import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import InspectionPhotosSimple from '../../components/InspectionPhotosSimple';
import InspectionInfo from '../../components/InspectionInfo/InspectionInfo';
import InspectionActions from '../../components/InspectionActions/InspectionActions';
import { useAuthContext } from '../../context/AuthContext';
import inspectionService from '../../services/inspectionService';
import './InspectionVerdictPage.css';
import { INSPECTION_STATUS_VERDICT, ROLES, INSPECTION_AREAS, INSPECTION_STATUS } from '../../utils/constants';
import { useSpinner } from '../../context/SpinnerContext';
import Modal from '../../components/Modal';
import { InspectionAreas } from '../../components/InspectionAreas';
import photoService from '../../services/photoService';
import Notification from '../../components/Notification';
import InspectionComments from '../../components/InspectionComments/InspectionComments';
import documentService from '../../services/documentService';
import InspectionReport from '../../components/InspectionReport';
import logger from '../../utils/logger';
import config from '../../config/environment';

const { APPROVED, REJECTED, IN_PROGRESS, PENDING } = INSPECTION_STATUS_VERDICT;
const { PENDING_REVIEW, PENDING_USER } = INSPECTION_STATUS;
const { INSPECTOR, ADMINISTRATOR, CREATOR, CREATOR_SENIOR } = ROLES;
const { ANTECEDENTS, VERDICTS } = INSPECTION_AREAS;

const InspectionVerdictPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuthContext();
    const { showSpinner, hideSpinner } = useSpinner();

    // Estado principal de la inspección
    const [inspection, setInspection] = useState(null);
    const [error, setError] = useState(null);
    const [notAllowed, setNotAllowed] = useState(false);
    const [isRevertResultModalOpen, setIsRevertResultModalOpen] = useState(false);
    const [revertResultArea, setRevertResultArea] = useState(null);
    const [notification, setNotification] = useState({ visible: false, message: '', type: 'info' });

    const [inspectionComments, setInspectionComments] = useState({ comments: [], attachments: [] });
    const [documentUrls, setDocumentUrls] = useState({});

    const hasLoadedRef = useRef(false);

    const loadInspection = useCallback(async () => {
        try {
            showSpinner('Cargando inspección...');
            logger.info('Cargando inspección...', {
                inspectionId: id,
            }, 'InspectionVerdictPage');
            setError(null);
            const result = await inspectionService.getInspection(id);
            const insuranceCompany = currentUser.insuranceCompany?.split(',') || [];
            if ([INSPECTOR, CREATOR, CREATOR_SENIOR].includes(currentUser.role) && !insuranceCompany.some(company => result.inspection.partnerData?.insuranceCompany.toLowerCase() === company.toLowerCase())) {
                setNotAllowed(true);
                return;
            }
            setInspection(result.inspection);

            // Cargar comentarios si existen
            if (result.inspection.inspectionComments) {
                setInspectionComments(result.inspection.inspectionComments);
                // Cargar URLs de descarga para archivos adjuntos
                if (result.inspection.inspectionComments.attachments?.length > 0) {
                    await loadDocumentUrls(result.inspection.id, result.inspection.inspectionComments.attachments);
                }
            }
        } catch (error) {
            logger.error('Error cargando inspección:', error, null, 'InspectionVerdictPage');
            setError(error.message);
        } finally {
            hideSpinner()
        }
    }, [id, currentUser.role, currentUser.insuranceCompany, showSpinner, hideSpinner]);

    // Cargar inspección al montar el componente
    useEffect(() => {
        if (id && !hasLoadedRef.current) {
            hasLoadedRef.current = true;
            loadInspection();
        }
    }, [id, loadInspection]);

    const handleInformationCorrected = (updatedInspection) => {
        setInspection((prev) => prev ? ({
            ...prev,
            vehicleData: updatedInspection.vehicleData ?? prev.vehicleData,
            contactData: updatedInspection.contactData ?? prev.contactData,
            partnerData: updatedInspection.partnerData ?? prev.partnerData,
            accidentType: Object.prototype.hasOwnProperty.call(updatedInspection, 'accidentType')
                ? updatedInspection.accidentType
                : prev.accidentType,
            inspectionComments: updatedInspection.inspectionComments ?? prev.inspectionComments,
            updatedAt: updatedInspection.updatedAt ?? prev.updatedAt,
        }) : updatedInspection);
        if (updatedInspection.inspectionComments) {
            setInspectionComments(updatedInspection.inspectionComments);
        }
    };

    const showNotification = (message, type = 'info') => {
        setNotification({ visible: true, message, type });
        setTimeout(() => {
            setNotification({ visible: false, message: '', type: 'info' });
        }, 5000);
    };

    // Función para actualizar la inspección en DynamoDB
    const updateInspectionInDynamo = async (updatedInspection) => {
        try {
            showSpinner('Actualizando inspección...');
            logger.info('Actualizando inspección...', {
                inspectionId: id,
            }, 'InspectionVerdictPage');
            const result = await inspectionService.updateInspection(id, updatedInspection);
            logger.info('Inspección actualizada exitosamente', {
                inspectionId: id,
            }, 'InspectionVerdictPage');
            setInspection(result.inspection);
            return result.inspection;
        } catch (error) {
            logger.error('Error actualizando inspección:', error, null, 'InspectionVerdictPage');
            throw error;
        } finally {
            hideSpinner();
        }
    };

    const loadDocumentUrls = async (inspectionId, attachments) => {
        try {
            const urls = {};

            const downloadUrl = await documentService.getDownloadUrl(inspectionId, attachments);
            downloadUrl.forEach(file => {
                urls[file.key] = file.downloadUrl;
            });

            setDocumentUrls(urls);
        } catch (error) {
            logger.error('Error cargando URLs de documentos:', error, null, 'InspectionVerdictPage');
        }
    };

    // Manejar envío de comentario
    const handleCommentSubmit = async (commentData, files) => {
        try {
            showSpinner('Guardando comentario...');
            logger.info('Guardando comentario...', {
                inspectionId: inspection?.id,
                commentData: commentData,
                filesCount: files?.length,
            }, 'InspectionVerdictPage');
            const currentInspection = (await inspectionService.getInspection(inspection.id)).inspection;
            const currentComments = currentInspection.inspectionComments;

            let uploadedAttachments = [];
            // Subir archivos si hay
            if (files && files.length > 0) {
                uploadedAttachments = await documentService.uploadDocuments(
                    files,
                    inspection.id,
                    (progress) => {
                        logger.info(`Subiendo archivos: ${progress}%`, null, 'InspectionVerdictPage');
                    }
                );
            }

            // Crear nuevo comentario
            const newComment = {
                id: Math.random().toString(36).substr(2, 6).toUpperCase(),
                author: {
                    name: currentUser.name,
                    role: currentUser.role
                },
                content: commentData.content,
                timestamp: new Date().toISOString(),
                attachments: uploadedAttachments
            };

            // Actualizar estado local
            const updatedComments = [...currentComments.comments, newComment];
            const updatedAttachments = [...currentComments.attachments, ...uploadedAttachments];

            const updatedInspectionComments = {
                comments: updatedComments,
                attachments: updatedAttachments
            };

            setInspectionComments(updatedInspectionComments);

            const updates = {
                inspectionComments: updatedInspectionComments
            };

            await updateInspectionInDynamo(updates);

            // Cargar URLs de descarga para los nuevos archivos
            if (uploadedAttachments.length > 0) {
                await loadDocumentUrls(inspection.id, uploadedAttachments);
            }

            showNotification('Comentario guardado exitosamente', 'success');
            logger.info('Comentario guardado exitosamente', {
                inspectionId: inspection?.id,
            }, 'InspectionVerdictPage');
        } catch (error) {
            logger.error('Error guardando comentario:', error, null, 'InspectionVerdictPage');
            showNotification('Error al guardar comentario', 'error');
        } finally {
            hideSpinner();
        }
    };

    // Descargar archivo
    const handleDownloadFile = async (attachment) => {
        try {
            let downloadUrl = documentUrls[attachment.id];
            logger.info('Descargando archivo...', {
                inspectionId: inspection?.id,
                attachmentId: attachment.id,
            }, 'InspectionVerdictPage');
            if (!downloadUrl) {
                downloadUrl = await documentService.getDownloadUrl(attachment.id);
                setDocumentUrls(prev => ({
                    ...prev,
                    [attachment.id]: downloadUrl
                }));
            }

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = attachment.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            logger.error('Error descargando archivo:', error, null, 'InspectionVerdictPage');
            showNotification('Error al descargar archivo', 'error');
        }
    };

    const approveRejectArea = async (area, action) => {
        const currentTime = new Date().toISOString();
        const userInfo = {
            name: currentUser?.name,
            email: currentUser?.email
        };

        const response = await inspectionService.getInspection(inspection.id);
        const currentInspection = response.inspection;

        const updates = {};

        if (area === ANTECEDENTS) {
            updates.antecedents = {
                ...currentInspection.antecedents,
                ...inspection.antecedents,
                status: action === APPROVED ? APPROVED : REJECTED,
                finishedBy: userInfo.name,
                finishedAt: currentTime
            };
        } else if (area === VERDICTS) {
            updates.verdict = {
                ...currentInspection.verdict,
                ...inspection.verdict,
                status: action === APPROVED ? APPROVED : REJECTED,
                finishedBy: userInfo.name,
                finishedAt: currentTime
            };
        }

        const updatedStatus = validateInspectionAreaCompleted({
            ...currentInspection,
            ...updates
        });
        updates.status = updatedStatus;

        try {
            await executeApproveRejectArea(updates, updatedStatus, currentInspection, userInfo);
            await loadInspection();
        } catch (error) {
            logger.error('Error al aprobar/rechazar área:', error, null, 'InspectionVerdictPage');
            showNotification('Error al procesar la acción. Inténtalo de nuevo.', 'error');
        }
    };

    const executeApproveRejectArea = async (updates, updatedStatus, currentInspection, userInfo) => {
        logger.info('Ejecutando aprobar/rechazar área...', {
            inspectionId: inspection?.id,
            updatedStatus: updatedStatus,
            currentInspection: currentInspection,
            userInfo: userInfo,
        }, 'InspectionVerdictPage');
        const currentTime = new Date().toISOString();

        if ([REJECTED, APPROVED].includes(updatedStatus)) {
            const updatedPhotos = (currentInspection.photos || []).map(photo => {
                const photoStatus = photo.status || PENDING;
                if (!photoStatus || photoStatus === PENDING || photoStatus === 'pendiente') {
                    return {
                        ...photo,
                        status: APPROVED,
                        finishedBy: userInfo.name,
                        finishedAt: currentTime
                    };
                }
                return photo;
            });
            updates.photos = updatedPhotos;
        }

        await updateInspectionInDynamo(updates);

        if ([REJECTED, APPROVED].includes(updatedStatus)) {
            await inspectionService.finalizeInspection(inspection.id);
        }
    };

    const validateInspectionAreaCompleted = (currentInspection) => {
        const antecedentsState = currentInspection.antecedents?.status;
        const verdictState = currentInspection.verdict?.status;
        const antecedentsDone = [APPROVED, REJECTED].includes(antecedentsState);
        const verdictDone = [APPROVED, REJECTED].includes(verdictState);
        return antecedentsDone && verdictDone
            ? (antecedentsState === APPROVED && verdictState === APPROVED ? APPROVED : REJECTED)
            : currentInspection.status;
    };

    const getAccidentInfo = () => {
        if (!inspection?.isAccident) return null;
        return {
            isAccident: true,
            type: inspection.accidentType === 'own' ? 'Propio' : inspection.accidentType === 'third_party' ? 'Tercero' : null
        };
    };

    // Funciones para manejar cambios en veredicto (SOLO ESTADO LOCAL)
    const handleVerdictChange = (field, value) => {
        if (!inspection || !isAreaAssignedToCurrentUser('verdict')) return;

        const updatedInspection = {
            ...inspection,
            verdict: {
                ...inspection.verdict,
                verdictFields: {
                    ...inspection.verdict?.verdictFields,
                    [field]: value
                }
            }
        };

        setInspection(updatedInspection);
    };

    const handleVerdictCommentsChange = (comments) => {
        if (!inspection || !isAreaAssignedToCurrentUser('verdict')) return;

        const updatedInspection = {
            ...inspection,
            verdict: {
                ...inspection.verdict,
                comments: comments
            }
        };

        setInspection(updatedInspection);
    };

    // Funciones para manejar cambios en antecedentes (SOLO ESTADO LOCAL)
    const handleAntecedentsChange = (hasAntecedents) => {
        if (!inspection || !isAreaAssignedToCurrentUser('antecedents')) return;

        const updatedInspection = {
            ...inspection,
            antecedents: {
                ...inspection.antecedents,
                hasAntecedents: hasAntecedents
            }
        };

        setInspection(updatedInspection);
    };

    const handleAntecedentsCommentsChange = (comments) => {
        if (!inspection || !isAreaAssignedToCurrentUser('antecedents')) return;

        const updatedInspection = {
            ...inspection,
            antecedents: {
                ...inspection.antecedents,
                antecedentsComments: comments
            }
        };

        setInspection(updatedInspection);
    };

    const handleCancelAntecedents = () => {
        // Limpiar campos de antecedentes
        const updatedInspection = {
            ...inspection,
            antecedents: {
                ...inspection.antecedents,
                hasAntecedents: undefined,
                antecedentsComments: ''
            }
        };
        setInspection(updatedInspection);
    };

    const handleCancelVerdict = () => {
        // Limpiar campos de veredicto
        const updatedInspection = {
            ...inspection,
            verdict: {
                ...inspection.verdict,
                verdictFields: {},
                comments: ''
            }
        };
        setInspection(updatedInspection);
    };

    // Funciones para aprobar/rechazar
    const handleApproveInspection = async (areaType) => {
        await approveRejectArea(areaType === 'inspection' ? VERDICTS : ANTECEDENTS, APPROVED);
    };

    const handleRejectInspection = async (areaType) => {
        await approveRejectArea(areaType === 'inspection' ? VERDICTS : ANTECEDENTS, REJECTED);
    };

    const isVerdictAreaComplete = () => {
        if (!inspection?.verdict?.verdictFields) return false;

        const requiredFields = ['chassis', 'engine', 'serialPlate'];

        // Verificar que todos los campos obligatorios estén completados
        const requiredComplete = requiredFields.every(field =>
            inspection.verdict.verdictFields[field] &&
            inspection.verdict.verdictFields[field].trim() !== ''
        );

        return requiredComplete;
    };

    const assignAreaToUser = async (area, userInfo) => {
        if (!inspection) return false;

        const fresh = (await inspectionService.getInspection(inspection.id)).inspection;
        const areaStatus = fresh?.[area]?.status;
        const inspectionClosed = [APPROVED, REJECTED].includes(fresh?.status);
        if (areaStatus !== PENDING || inspectionClosed) {
            setInspection(fresh);
            showNotification('El área ya fue tomada o la inspección ya finalizó. Se actualizó la pantalla.', 'error');
            return false;
        }

        const currentTime = new Date().toISOString();
        logger.info('Asignando área al usuario...', {
            inspectionId: inspection?.id,
            area: area,
            userInfo: userInfo,
        }, 'InspectionVerdictPage');

        const updates = {
            [area]: {
                ...fresh[area],
                assignedTo: userInfo,
                assignedAt: currentTime,
                status: IN_PROGRESS
            }
        };

        try {
            await updateInspectionInDynamo(updates);
            return true;
        } catch (error) {
            const reloaded = (await inspectionService.getInspection(inspection.id)).inspection;
            setInspection(reloaded);
            showNotification(error.message || 'No se pudo iniciar el área', 'error');
            return false;
        }
    };

    const mergeOfficialRecordsIntoInspection = (officialRecords) => {
        setInspection(prev => ({
            ...prev,
            antecedents: {
                ...prev.antecedents,
                officialRecords,
            }
        }));
    };

    const consultOfficialRecordsAndMerge = async (payload) => {
        try {
            showSpinner('Consultando antecedentes...');
            const result = await inspectionService.consultOfficialRecords(id, payload);
            mergeOfficialRecordsIntoInspection(result.officialRecords);
        } catch (error) {
            logger.error('Error consultando antecedentes oficiales:', error, null, 'InspectionVerdictPage');
            mergeOfficialRecordsIntoInspection(error.officialRecords || {
                error: {
                    message: error.message || 'Error al consultar los antecedentes oficiales',
                    at: new Date().toISOString(),
                    consultBy: payload?.consultBy,
                }
            });
        } finally {
            hideSpinner();
        }
    };

    // Función para verificar si el usuario puede gestionar un área
    const canManageArea = (area) => {

        if (!currentUser || !inspection) return false;

        // Administradores siempre pueden gestionar
        if (currentUser.role === ADMINISTRATOR) return true;

        // Inspectores solo si tienen permisos y el área no está asignada
        if (currentUser.role === INSPECTOR) {
            const areaData = area === ANTECEDENTS ? inspection.antecedents : inspection.verdict;

            // Verificar si tiene permisos para esta área
            const hasPermission = currentUser.inspectionAreas?.includes(area === ANTECEDENTS ? ANTECEDENTS : VERDICTS);

            // Verificar si el área no está asignada a otro usuario
            const isActivelyAssigned = areaData?.assignedTo && areaData?.status === IN_PROGRESS;
            const notAssigned = !isActivelyAssigned;

            return hasPermission && notAssigned;
        }

        return false;
    };

    // Función para verificar si el área está asignada al usuario actual
    const isAreaAssignedToCurrentUser = (area) => {
        if (!currentUser || !inspection) return false;

        const areaData = area === ANTECEDENTS ? inspection.antecedents : inspection.verdict;
        return areaData?.assignedTo?.email === currentUser.email;
    };

    const enableRevertResultButton = (area) => {
        const inspectionArea = area === ANTECEDENTS ? inspection.antecedents : inspection.verdict;
        if (
            currentUser.role === ADMINISTRATOR &&
            [APPROVED, REJECTED, IN_PROGRESS].includes(inspectionArea?.status)
        ) {
            return true;
        }
        return false;
    };

    // Función para manejar inicio de área
    const handleStartArea = async (area, payload) => {
        if (!canManageArea(area)) return;

        const userInfo = {
            name: currentUser?.name,
            email: currentUser?.email
        };

        const started = await assignAreaToUser(area, userInfo);
        if (!started) return;
        if (area === ANTECEDENTS && config.ENABLE_OFFICIAL_RECORDS) {
            await consultOfficialRecordsAndMerge(payload);
        }
    };

    const handleRetryPlacApi = async (payload) => {
        await consultOfficialRecordsAndMerge(payload);
    };

    const enableApproveRejectButtons = (area) => {
        const areaData = area === ANTECEDENTS ? inspection.antecedents : inspection.verdict;
        if (currentUser.email.toLowerCase() === areaData?.assignedTo?.email.toLowerCase() && areaData?.status === IN_PROGRESS) {
            return true;
        }
        return false;
    };

    const enableStartButton = (area) => {
        const { antecedents, verdict } = inspection;
        const areaData = area === ANTECEDENTS ? antecedents : verdict;

        if ((currentUser.role === 'Administrador' || (currentUser.role === 'Inspector' && currentUser.inspectionAreas?.includes(area))) && areaData?.status === PENDING) {
            return true;
        }
        return false;
    };

    const handlePhotoRejections = async (photoUpdates) => {
        if (!inspection || photoUpdates.length === 0) return;

        try {
            showSpinner('Actualizando rechazos de fotos...');
            logger.info('Actualizando rechazos de fotos...', {
                inspectionId: inspection?.id,
                photoUpdatesCount: photoUpdates?.length,
                currentUserEmail: currentUser?.email,
            }, 'InspectionVerdictPage');
            
            // Obtener inspección actualizada desde el servidor
            const currentInspection = (await inspectionService.getInspection(inspection.id)).inspection;
            
            // Validar estado actual de las áreas antes de establecer nuevo status
            const currentStatus = validateInspectionAreaCompleted(currentInspection);
            
            // Solo establecer PENDING_USER si las áreas no están finalizadas
            const newStatus = ([APPROVED, REJECTED].includes(currentStatus)) 
                ? currentStatus 
                : PENDING_USER;
            
            const updates = {
                photos: photoUpdates,
                status: newStatus
            };

            await updateInspectionInDynamo(updates);
            showSpinner('Rechazos de fotos actualizados exitosamente');

            logger.info('Rechazos de fotos actualizados exitosamente', {
                inspectionId: inspection?.id,
            }, 'InspectionVerdictPage');

            // Notificar fotos rechazadas
            const rejectedPhotos = photoUpdates.filter(photo => photo.status === REJECTED).map(photo => ({ photoName: photo.name, rejectionReason: photo.rejectionReason }));
            await inspectionService.notifyPhotosRejected(inspection.id, rejectedPhotos);
            logger.info('Fotos rechazadas notificadas exitosamente', {
                inspectionId: inspection?.id,
                rejectedPhotosCount: rejectedPhotos?.length,
            }, 'InspectionVerdictPage');
        } catch (error) {
            logger.error('Error actualizando rechazos de fotos:', error, null, 'InspectionVerdictPage');
            hideSpinner();
            setError(error.message);
        } finally {
            hideSpinner();
        }
    };

    const handlePhotoUpdate = async (photosToUpdate, newPhotoData) => {
        showSpinner('Actualizando fotos...');
        logger.info('Actualizando fotos...', {
            inspectionId: inspection?.id,
        }, 'InspectionVerdictPage');
        try {
            const anyRejected = photosToUpdate.some(photo => photo.status === REJECTED);
            
            // Obtener inspección actualizada desde el servidor
            const currentInspection = (await inspectionService.getInspection(inspection.id)).inspection;
            
            // Validar estado actual de las áreas antes de establecer nuevo status
            const currentStatus = validateInspectionAreaCompleted(currentInspection);
            
            // Determinar el nuevo status considerando áreas y fotos rechazadas
            let newStatus;
            if ([APPROVED, REJECTED].includes(currentStatus)) {
                // Si las áreas ya están finalizadas, mantener ese estado
                newStatus = currentStatus;
            } else {
                // Si las áreas no están finalizadas, usar lógica de fotos
                newStatus = anyRejected ? PENDING_USER : PENDING_REVIEW;
            }

            const updates = {
                photos: photosToUpdate,
                status: newStatus
            };

            if (!anyRejected && newStatus === PENDING_REVIEW) {
                updates.readyForReviewAt = new Date().toISOString();
            }

            try {
                await photoService.updateExistingPhoto(newPhotoData.file, inspection.id, newPhotoData.id, (progress) => {
                    console.log(`Foto ${progress.toFixed(1)}%`);
                });
            } catch (uploadError) {
                throw uploadError;
            }

            await updateInspectionInDynamo(updates);

            showNotification('Fotos actualizadas exitosamente', 'success');

            return {
                success: true,
                photos: updates.photos
            }

        } catch (error) {
            logger.error('Error actualizando fotos:', error, null, 'InspectionVerdictPage');
            hideSpinner();
            showNotification('Error al actualizar las fotos. Inténtalo de nuevo.', 'error');
        }
    };

    const handleRevertResult = async (area) => {
        try {
            setIsRevertResultModalOpen(false);
            showSpinner('Revertir resultado...');
            logger.info('Revertir resultado...', {
                inspectionId: inspection?.id,
                area: area,
                currentUserEmail: currentUser?.email,
            }, 'InspectionVerdictPage');
            const inspectionArea = area === ANTECEDENTS ? 'antecedents' : 'verdict';

            const revertedAreaState = area === ANTECEDENTS
                ? { status: PENDING, antecedentsComments: '' }
                : { status: PENDING, verdictFields: {}, comments: '' };

            const updates = {
                [inspectionArea]: revertedAreaState,
                status: inspection.status === PENDING_USER ? PENDING_USER : PENDING_REVIEW,
                finishedAt: null
            };

            await updateInspectionInDynamo(updates);

            setRevertResultArea(null);
        } catch (error) {
            logger.error('Error revertiendo resultado:', error, null, 'InspectionVerdictPage');
            setError(error.message);
        } finally {
            hideSpinner();
        }
    };

    const handleMissingPhotosRequest = async (newRequest) => {
        if (!inspection) return;

        try {
            showSpinner('Registrando solicitud de fotos faltantes...');
            logger.info('Registrando solicitud de fotos faltantes...', {
                inspectionId: inspection?.id,
                currentUserEmail: currentUser?.email,
            }, 'InspectionVerdictPage');
            
            // Obtener inspección actualizada desde el servidor
            const currentInspection = (await inspectionService.getInspection(inspection.id)).inspection;
            
            // Validar estado actual de las áreas antes de establecer nuevo status
            const currentStatus = validateInspectionAreaCompleted(currentInspection);
            
            // Solo establecer PENDING_USER si las áreas no están finalizadas
            const newStatus = ([APPROVED, REJECTED].includes(currentStatus)) 
                ? currentStatus 
                : PENDING_USER;
            
            const updates = {
                missingPhotosRequests: [
                    ...(currentInspection.missingPhotosRequests || []),
                    newRequest
                ],
                status: newStatus
            };

            await updateInspectionInDynamo(updates);
            showSpinner('Solicitud de fotos faltantes registrada...');

            await inspectionService.requestAdditionalPhotos(inspection.id, newRequest.comments, currentUser.name);

            showNotification('Solicitud de fotos faltantes registrada exitosamente', 'success');

        } catch (error) {
            logger.error('Error requesting missing photos:', error, null, 'InspectionVerdictPage');
            showNotification('Error al registrar la solicitud. Inténtalo de nuevo.', 'error');
            throw error;
        } finally {
            hideSpinner();
        }
    };

    const handleAddMissingPhotos = async (newPhotosData, newPhotosFiles) => {
        if (!inspection || newPhotosFiles.length === 0) return;

        try {
            showSpinner('Agregando fotos faltantes...');
            logger.info('Agregando fotos faltantes...', {
                inspectionId: inspection?.id,
                currentUserEmail: currentUser?.email,
            }, 'InspectionVerdictPage');
            await photoService.uploadMissingPhotos(newPhotosFiles, inspection.id, (progress) => {
                console.log(`Foto ${progress.toFixed(1)}%`);
            });

             // Obtener inspección actualizada desde el servidor
             const currentInspection = (await inspectionService.getInspection(inspection.id)).inspection;
            
             // Validar estado actual de las áreas antes de establecer nuevo status
             const currentStatus = validateInspectionAreaCompleted(currentInspection);
             
             // Solo establecer PENDING_REVIEW si las áreas no están finalizadas
             const newStatus = ([APPROVED, REJECTED].includes(currentStatus)) 
                 ? currentStatus 
                 : PENDING_REVIEW;
             
             const updates = {
                 photos: [
                     ...currentInspection.photos,
                     ...newPhotosData
                 ],
                 missingPhotosRequests: currentInspection.missingPhotosRequests?.map(request => ({
                     ...request,
                     status: 'resolved'
                 })) || [],
                 status: newStatus,
                 readyForReviewAt: newStatus === PENDING_REVIEW ? new Date().toISOString() : currentInspection.readyForReviewAt
             };
 
             await updateInspectionInDynamo(updates);

            showNotification('Fotos faltantes agregadas exitosamente', 'success');
            return {
                success: true,
                photos: updates.photos
            }

        } catch (error) {
            logger.error('Error adding missing photos:', error, null, 'InspectionVerdictPage');
            showNotification('Error al agregar las fotos. Inténtalo de nuevo.', 'error');
            throw error;
        } finally {
            hideSpinner();
        }
    };

    const handleForcedRejectInspection = async (commentData) => {
        if (!inspection) return;
        showSpinner('Rechazando inspección...');
        logger.info('Rechazando inspección...', {
            inspectionId: inspection?.id,
            currentUserEmail: currentUser?.email,
        }, 'InspectionVerdictPage');
        try {
            const newComment = {
                id: Math.random().toString(36).substr(2, 6).toUpperCase(),
                author: {
                    name: currentUser.name,
                    role: currentUser.role
                },
                content: commentData.content,
                timestamp: new Date().toISOString(),
                attachments: []
            };

            const updatedComments = [...inspectionComments.comments, newComment];
            const updatedAttachments = [...inspectionComments.attachments, ...[]];

            const updatedInspectionComments = {
                comments: updatedComments,
                attachments: updatedAttachments
            };

            setInspectionComments(updatedInspectionComments);

            const updates = {
                inspectionComments: updatedInspectionComments,
                status: REJECTED
            };

            await updateInspectionInDynamo(updates);

        } catch (error) {
            logger.error('Error rejecting inspection:', error, null, 'InspectionVerdictPage');
            showNotification('Error al rechazar la inspección. Inténtalo de nuevo.', 'error');
            throw error;
        } finally {
            hideSpinner();
        }
    };

    const handleDownloadPhotosZip = async (inspectionId) => {
        if (!inspection || !inspection.photos || inspection.photos.length === 0) {
            showNotification('No hay fotos para descargar', 'error');
            return;
        }

        try {
            showSpinner('Generando archivo ZIP de fotos...');
            logger.info('Generando archivo ZIP de fotos...', {
                inspectionId: inspectionId,
            }, 'InspectionVerdictPage');
            const response = await photoService.downloadPhotosZip(inspectionId);

            if (response.success && response.downloadUrl) {
                const link = document.createElement('a');
                link.href = response.downloadUrl;
                link.download = response.zipFileName || `${inspectionId}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                showNotification('Archivo ZIP descargado exitosamente', 'success');
                logger.info('ZIP descargado exitosamente:', {
                    inspectionId: inspectionId,
                    zipFileName: response.zipFileName,
                }, 'InspectionVerdictPage');
            } else {
                throw new Error(response.error || 'Error generando el archivo ZIP');
            }

        } catch (error) {
            logger.error('Error downloading photos ZIP:', error, null, 'InspectionVerdictPage');
            showNotification(error.message || 'Error al descargar las fotos', 'error');
        } finally {
            hideSpinner();
        }
    };

    const handleDownloadPhoto = async (photo) => {
        if (!photo || !photo.key) {
            showNotification('No se pudo obtener la información de la foto', 'error');
            return;
        }

        try {
            showSpinner('Generando descarga de foto...');
            logger.info('Generando descarga de foto...', {
                inspectionId: inspection?.id,
                photoId: photo.id,
            }, 'InspectionVerdictPage');
            // Llamar al servicio para generar URL de descarga
            const response = await photoService.downloadPhoto(inspection.id, photo.key);

            if (response.success && response.downloadUrl) {
                // Crear un enlace temporal para descargar el archivo
                const link = document.createElement('a');
                link.href = response.downloadUrl;
                link.download = response.fileName || photo.name || 'foto.jpg';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                showNotification('Foto descargada exitosamente', 'success');
                logger.info('Foto descargada exitosamente:', {
                    inspectionId: inspection?.id,
                    photoId: photo.id,
                    fileName: response.fileName,
                }, 'InspectionVerdictPage');
            } else {
                throw new Error(response.error || 'Error generando la descarga de la foto');
            }

        } catch (error) {
            logger.error('Error downloading photo:', error, null, 'InspectionVerdictPage');
            showNotification(error.message || 'Error al descargar la foto', 'error');
        } finally {
            hideSpinner();
        }
    };

    // Error state
    if (error) {
        return (
            <div className="inspection-verdict-page">
                <Header />
                <main className="verdict-content-wrapper">
                    <div className="error-container">
                        <p>Error: {error}</p>
                        <button onClick={loadInspection}>Reintentar</button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (notAllowed) {

        return (
            <div className="inspection-verdict-page">
                <Header />
                <main className="verdict-content-wrapper">
                    <div className="error-container">
                        <p>No tienes permisos para ver esta inspección</p>
                        <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Volver al dashboard</button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // Si no hay inspección
    if (!inspection) {
        return (
            <div className="inspection-verdict-page">
                <Header />
                <main className="verdict-content-wrapper">
                    <div className="error-container">
                        <p>No se encontró la inspección</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="inspection-verdict-page">
            <Header />

            <main className="verdict-content-wrapper">
                {/* Page Title */}
                <div className="page-title-section">
                    <div className="title-content">
                        <div className="title-row">
                            <svg className="verdict-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 12l2 2 4-4"></path>
                                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"></path>
                            </svg>
                            <h1>Módulo de Dictamen Técnico Legal</h1>
                            {getAccidentInfo() && (
                                <span className="accident-badge" title={`Siniestro: ${getAccidentInfo().type}`}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                    </svg>
                                    Siniestro {getAccidentInfo().type}
                                </span>
                            )}
                        </div>
                        <p className="page-subtitle">Realiza el estudio de antecedentes, daños y dictamen de la inspección vehicular</p>
                    </div>
                </div>

                {/* Inspection Info Section */}
                <div className="section-card">
                    <h2 className="section-title">Información de la Inspección</h2>
                    <InspectionInfo
                        inspectionId={id}
                        inspectionData={inspection}
                        onInspectionUpdate={handleInformationCorrected}
                    />
                </div>

                {/* Inspection Report Section */}
                {[APPROVED, REJECTED].includes(inspection.status) && [ADMINISTRATOR, CREATOR_SENIOR].includes(currentUser.role) && (
                    <div className="section-card">
                        <InspectionReport
                            inspectionId={inspection.id}
                            inspectionStatus={inspection.status}
                        />
                    </div>
                )}

                {/* Inspection Photos Section */}
                <div className="section-card">
                    <h2 className="section-title">Fotografías del Vehículo</h2>
                    <InspectionPhotosSimple
                        inspectionId={id}
                        inspectionPhotos={inspection.photos}
                        onPhotoRejections={handlePhotoRejections}
                        inspectionStatus={inspection.status}
                        onPhotoUpdate={handlePhotoUpdate}
                        onMissingPhotosRequest={handleMissingPhotosRequest}
                        onAddMissingPhotos={handleAddMissingPhotos}
                        onDownloadPhotosZip={handleDownloadPhotosZip}
                        onDownloadPhoto={handleDownloadPhoto}
                        missingPhotosRequests={inspection.missingPhotosRequests || []}
                    />
                </div>

                {/* Inspection Areas Section */}
                <InspectionAreas
                    inspection={inspection}
                    currentUser={currentUser}
                    onVerdictChange={handleVerdictChange}
                    onVerdictCommentsChange={handleVerdictCommentsChange}
                    onAntecedentsChange={handleAntecedentsChange}
                    onAntecedentsCommentsChange={handleAntecedentsCommentsChange}
                    onStartArea={handleStartArea}
                    onCancelAntecedents={handleCancelAntecedents}
                    onCancelVerdict={handleCancelVerdict}
                    onApproveInspection={handleApproveInspection}
                    onRejectInspection={handleRejectInspection}
                    onRevertResult={handleRevertResult}
                    enableApproveRejectButtons={enableApproveRejectButtons}
                    enableStartButton={enableStartButton}
                    enableRevertResultButton={enableRevertResultButton}
                    isVerdictAreaComplete={isVerdictAreaComplete}
                    rejectedPhotosCount={inspection.photos?.filter(p => p.status === REJECTED || p.status === 'rechazado').length || 0}
                    pendingPhotosCount={inspection.photos?.filter(p => !p.status || p.status === PENDING || p.status === 'pendiente').length || 0}
                    onRetryPlacApi={handleRetryPlacApi}
                />

                <InspectionComments
                    isDisabled={[APPROVED, REJECTED].includes(inspection.status)}
                    inspectionId={inspection.id}
                    inspectionComments={inspectionComments}
                    documentUrls={documentUrls}
                    onCommentSubmit={handleCommentSubmit}
                    onDownloadFile={handleDownloadFile}
                />

                <Modal
                    title="Revertir Resultado"
                    isOpen={isRevertResultModalOpen}
                    onClose={() => setIsRevertResultModalOpen(false)}
                    size="small"
                    showCloseButton={false}
                    closeOnBackdropClick={true}
                >
                    <p>¿Estás seguro de querer revertir el resultado?</p>
                    <p className="modal-warning">Importante: Esta acción no se puede deshacer.</p>
                    <div className="modal-actions">
                        <button className="revert-result-button btn-primary cancel-button" onClick={() => setIsRevertResultModalOpen(false)}>
                            Cancelar
                        </button>
                        <button className="revert-result-button btn-primary" onClick={() => handleRevertResult(revertResultArea)}>
                            Confirmar
                        </button>
                    </div>
                </Modal>

                {/* Inspection Actions */}
                <InspectionActions
                    inspectionId={id}
                    onRejectInspection={handleForcedRejectInspection}
                    inspectionStatus={inspection.status}
                />

                {/* Notification Component */}
                <Notification
                    visible={notification.visible}
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification({ visible: false, message: '', type: 'info' })}
                />
            </main>

            <Footer />
        </div>
    );
};

export default InspectionVerdictPage;